/**
 * LiveMessage design tokens v2 — deux thèmes, une grammaire.
 *
 * Règles :
 *  R1 — le lime est un événement (présence, frappe live, action primaire, lu) ;
 *       jamais une couleur de texte courant.
 *  R2 — le mono est l'encre du direct ; l'interface parle en sans humaniste.
 *  R3 — la profondeur vient de la teinte (surfaces vert-noir stratifiées).
 */

/** Rayons de la grammaire v2. */
export const radius = {
  field: 14,
  bubble: 18,
  sheet: 24,
  pill: 999,
} as const;

/**
 * Chrome de navigation (UX-5). `h` est la hauteur PLANCHER de la tab bar —
 * le chrome grandit avec la police de l'utilisateur, il ne descend jamais
 * sous cette valeur. Le littéral 54 recopié à la main est interdit (lint UX-9).
 */
export const nav = {
  h: 54,
} as const;

/**
 * Voiles d'assombrissement (UX-7). L'audit a compté NEUF rgba noirs en dur
 * (0.5 → 0.94) pour deux intentions seulement : le fond d'une modale/sheet
 * (`scrim`) et le voile d'attente d'un plateau (`veil`). Deux tokens, plus
 * jamais un littéral — la visionneuse photo (0.94) reste un cas à part
 * assumé, c'est de l'immersion, pas un voile.
 */
export const overlay = {
  scrim: 'rgba(0,0,0,0.55)',
  veil: 'rgba(0,0,0,0.5)',
  /**
   * UX-10 — l'ombre portée des éléments flottants (bannières). L'ombre reste
   * noire dans les deux thèmes (c'est une ombre, pas une encre) ; la nommer
   * ici évite le `#000` en dur que le lint interdit désormais.
   */
  shadow: '#000000',
} as const;

/**
 * Largeurs de colonne desktop (UX-7). Sept littéraux relevés (450 → 800)
 * pour trois intentions : formulaire (`narrow`), contenu (`base`), liste
 * riche (`wide`). Un écran routé sans colonne est un écran cassé en desktop
 * — le test de contrat d'UX-15 le refuse.
 */
export const col = {
  narrow: 480,
  base: 560,
  wide: 800,
} as const;

/**
 * Durées de mouvement (UX-7) — le vocabulaire fermé du motion, jumeau des
 * intentions haptiques : une micro-réaction, une transition d'écran, une
 * mise en scène. Toute animation hors de ces trois durées se justifie par
 * écrit (les jeux ont leur kit, c'est leur territoire).
 */
export const motion = {
  rapide: 140,
  standard: 240,
  lente: 400,
} as const;

/** Familles typographiques (chargées via expo-font dans App.tsx). */
export const font = {
  /** Interface — Instrument Sans */
  ui: "InstrumentSans",
  uiMedium: "InstrumentSans-Medium",
  uiSemiBold: "InstrumentSans-SemiBold",
  uiBold: "InstrumentSans-Bold",
  /** Texte vivant + métadonnées techniques — JetBrains Mono */
  live: "JetBrainsMono",
  liveBold: "JetBrainsMono_Bold",
} as const;

/** Thème sombre — défaut. */
export const dark = {
  bg: "#0B0D09",
  surface: "#12150F",
  surface2: "#1A1E15",
  surface3: "#232819",
  line: "rgba(237,241,228,0.09)",
  lineStrong: "rgba(237,241,228,0.16)",
  ink: "#EDF1E4",
  ink2: "#A9B29A",
  ink3: "#6E7660",
  accent: "#C8FF64",
  accentPress: "#A8DB48",
  onAccent: "#131802",
  /** Encre du texte en train de s'écrire */
  liveInk: "#C8FF64",
  glow: "rgba(200,255,100,0.28)",
  /**
   * UX-24 — l'encre de la MARQUE, et elle seule (logo, tagline tapée du Login).
   * POURQUOI un token de plus alors qu'il vaut exactement `liveInk` : depuis
   * qu'on choisit sa couleur d'accent, `liveInk` bouge. Le wordmark du Login
   * était peint avec lui : choisir « Rose » repeignait donc l'identité de
   * LiveMessage. La marque n'est pas un accent — elle ne suit AUCUNE palette.
   */
  brand: "#C8FF64",
  error: "#FF6B5E",
  warning: "#E8A13A",
  /**
   * UX-7 — les DÉRIVÉS, enfin par thème. L'audit a trouvé leurs rgba figés
   * en dur dans les écrans : dérivés du thème SOMBRE, ils mentaient en clair
   * (Button danger, bordure danger de ContactCardSheet…). Ici, chaque thème
   * porte les siens — un écran ne calcule plus jamais un alpha à la main.
   */
  accentBorder: "rgba(200,255,100,0.45)",
  errorBorder: "rgba(255,107,94,0.35)",
  errorPress: "rgba(255,107,94,0.12)",
  warningSoft: "rgba(232,161,58,0.14)",
  warningPress: "#cf8e2f",
  onWarning: "#1a1202",
} as const;

/** Thème clair — jumeau (branchement runtime à venir). */
export const light = {
  bg: "#F5F7F0",
  surface: "#FFFFFF",
  surface2: "#ECEFE3",
  surface3: "#E0E4D3",
  line: "rgba(26,30,18,0.10)",
  lineStrong: "rgba(26,30,18,0.18)",
  ink: "#1A1E12",
  ink2: "#5B6350",
  ink3: "#8B937D",
  accent: "#C8FF64",
  accentPress: "#B2E852",
  onAccent: "#131802",
  liveInk: "#3E5C00",
  glow: "rgba(122,168,32,0.25)",
  /** UX-24 — jumeau clair de l'encre de marque (voir le thème sombre). */
  brand: "#3E5C00",
  error: "#C93D30",
  warning: "#9A6516",
  /** UX-7 — dérivés du thème CLAIR (voir le pavé jumeau du thème sombre). */
  accentBorder: "rgba(122,168,32,0.45)",
  errorBorder: "rgba(201,61,48,0.35)",
  errorPress: "rgba(201,61,48,0.12)",
  warningSoft: "rgba(154,101,22,0.14)",
  warningPress: "#7d5212",
  onWarning: "#FFFFFF",
} as const;

export type Theme = { [K in keyof typeof dark]: string };

/** Les deux modes d'affichage — le mode, pas la palette d'accent. */
export type ThemeScheme = "dark" | "light";

/**
 * UX-24 — les SIX valeurs qu'une palette d'accent porte. La spec d'origine
 * n'en voyait qu'une (« la couleur ») : c'est faux, et c'est le piège qui
 * coûte l'US. `liveInk` en clair n'est PAS dérivable de `accent` par calcul —
 * le `#3E5C00` du lime est un choix à la main, assombri jusqu'à passer l'AA
 * sur `bg` ET sur `surface`. Chaque palette choisit donc le sien.
 */
export type AccentSlots = Pick<
  Theme,
  "accent" | "accentPress" | "onAccent" | "liveInk" | "glow" | "accentBorder"
>;

/** Les palettes CURATÉES — vocabulaire fermé, pas de couleur libre. */
export type AccentName = "lime" | "ciel" | "rose" | "ambre";

/** Ordre d'affichage du réglage : le défaut d'abord. */
export const ACCENT_NAMES = ["lime", "ciel", "rose", "ambre"] as const;

export const DEFAULT_ACCENT: AccentName = "lime";

/**
 * UX-24 — les quatre palettes d'accent au choix de l'utilisateur.
 *
 * POURQUOI CURATÉES, et pas un sélecteur de couleur libre : R1 tient parce que
 * l'accent est LISIBLE partout où il porte du texte (`onAccent` sur `accent`,
 * `liveInk` sur `bg` et sur `surface`). Un sélecteur libre livre à
 * l'utilisateur le droit de se rendre l'app illisible. Ici chaque palette
 * passe un contrôle AA automatique — `apps/mobile/src/__tests__/palettes.contrast.test.ts`
 * refuse la palette, pas l'écran. Une palette qui échoue NE RENTRE PAS.
 *
 * PÉRIMÈTRE EXCLU, volontairement et définitivement :
 *  - la MARQUE (`brand`, le splash, les icônes d'app) — elle ne suit aucune
 *    palette, sinon l'utilisateur repeint l'identité de LiveMessage ;
 *  - le verre des jeux (`retro/COLOR_PALETTE`, `SCENE_TINT`) reste figé ; seule
 *    la Game Boy (`makeDmgPalette`) suit `tokens.accent`, et c'est VOULU ;
 *  - les e-mails (rendus serveur, hors thème) ;
 *  - les canaux de notification Android (`lightColor` est GELÉ à la création du
 *    canal par le système : il ne changera jamais, palette ou pas) ;
 *  - `app.json` (le `#C8FF64` du plugin de notifications est figé au build) ;
 *  - `apps/admin`, `apps/monitoring`, `apps/api` — hors app, hors lint.
 */
export const accents: Record<AccentName, Record<ThemeScheme, AccentSlots>> = {
  /** Lime — le défaut : EXACTEMENT les valeurs historiques des deux thèmes. */
  lime: {
    dark: {
      accent: "#C8FF64",
      accentPress: "#A8DB48",
      onAccent: "#131802",
      liveInk: "#C8FF64",
      glow: "rgba(200,255,100,0.28)",
      accentBorder: "rgba(200,255,100,0.45)",
    },
    light: {
      accent: "#C8FF64",
      accentPress: "#B2E852",
      onAccent: "#131802",
      liveInk: "#3E5C00",
      glow: "rgba(122,168,32,0.25)",
      accentBorder: "rgba(122,168,32,0.45)",
    },
  },
  /** Ciel — le cyan froid ; `liveInk` clair descendu au bleu-canard pour l'AA. */
  ciel: {
    dark: {
      accent: "#64D8FF",
      accentPress: "#48B4DB",
      onAccent: "#001822",
      liveInk: "#64D8FF",
      glow: "rgba(100,216,255,0.28)",
      accentBorder: "rgba(100,216,255,0.45)",
    },
    light: {
      accent: "#64D8FF",
      accentPress: "#52C0E8",
      onAccent: "#001822",
      liveInk: "#005A78",
      glow: "rgba(32,140,168,0.25)",
      accentBorder: "rgba(32,140,168,0.45)",
    },
  },
  /** Rose — le magenta doux ; `liveInk` clair en framboise profonde. */
  rose: {
    dark: {
      accent: "#FF9EC4",
      accentPress: "#DB7FA5",
      onAccent: "#2B0414",
      liveInk: "#FF9EC4",
      glow: "rgba(255,158,196,0.28)",
      accentBorder: "rgba(255,158,196,0.45)",
    },
    light: {
      accent: "#FF9EC4",
      accentPress: "#E884AC",
      onAccent: "#2B0414",
      liveInk: "#9B0A46",
      glow: "rgba(176,32,90,0.25)",
      accentBorder: "rgba(176,32,90,0.45)",
    },
  },
  /**
   * Ambre — le jaune chaud. Il FRÔLE `warning` (#E8A13A / #9A6516) : c'est
   * assumé, `warning` ne vit jamais nu (il arrive avec `warningSoft` en fond
   * et son libellé), là où l'accent est une action.
   */
  ambre: {
    dark: {
      accent: "#FFC864",
      accentPress: "#DBA648",
      onAccent: "#1F1400",
      liveInk: "#FFC864",
      glow: "rgba(255,200,100,0.28)",
      accentBorder: "rgba(255,200,100,0.45)",
    },
    light: {
      accent: "#FFC864",
      accentPress: "#E8B052",
      onAccent: "#1F1400",
      liveInk: "#6B4400",
      glow: "rgba(168,120,32,0.25)",
      accentBorder: "rgba(168,120,32,0.45)",
    },
  },
};

/** Une valeur venue du stockage local est-elle une palette connue ? */
export function isAccentName(value: unknown): value is AccentName {
  return typeof value === "string" && (ACCENT_NAMES as readonly string[]).includes(value);
}

/**
 * UX-10 — les alias v1 (`accent90/60/30/15/08`, `warning15`, `mono`) sont
 * SUPPRIMÉS : la dépréciation était devenue une invitation (44 usages
 * relevés par l'audit). Le lint (UX-9/aliasV1) interdit leur retour.
 * `makeTokens` survit sans eux : l'admin l'importe.
 *
 * UX-24 — elle compose désormais un mode et une palette d'accent, et le second
 * argument est OPTIONNEL : `makeTokens(dark)` rend exactement ce qu'il rendait
 * avant (les deux thèmes portent déjà les valeurs du lime), donc l'admin — qui
 * l'importe aussi — n'a rien à changer. Les slots arrivent RÉSOLUS pour le mode
 * plutôt que sous forme de nom : un `Theme` ne sait pas s'il est clair ou
 * sombre, et le deviner par identité d'objet casserait en silence sur une
 * copie. Le raccourci qui fait les deux d'un coup est `themeTokens`.
 */
export const makeTokens = (p: Theme, accent?: AccentSlots) => ({ ...p, ...accent });

/** Les tokens d'un mode × une palette — le point d'entrée du ThemeProvider. */
export const themeTokens = (scheme: ThemeScheme, palette: AccentName = DEFAULT_ACCENT) =>
  makeTokens(scheme === "light" ? light : dark, accents[palette][scheme]);

export const darkTokens = makeTokens(dark);
export const lightTokens = makeTokens(light);

/** Tokens du thème sombre — import statique hérité ; préférer useTheme(). */
export const tokens = darkTokens;

export type ThemeTokens = ReturnType<typeof makeTokens>;
export type Tokens = typeof tokens;
